import { onUnmounted } from 'vue'
import axios from 'axios'
import { sessionExpiresAt } from '@/api/client'
import { getConfig } from '@/config'
import { useAuthStore } from '@/stores/auth.store'

const DEBOUNCE_MS = 60_000
const REFRESH_THRESHOLD_MS = 30 * 60 * 1000

export function useSessionRefresh() {
  let lastCheck = 0
  let refreshing = false
  const events = ['click', 'keydown', 'scroll', 'touchstart'] as const

  async function tryRefresh() {
    const now = Date.now()
    if (now - lastCheck < DEBOUNCE_MS) return
    lastCheck = now
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) return
    const expiresAt = sessionExpiresAt.value
    if (expiresAt === null) return
    const remaining = expiresAt * 1000 - now
    if (remaining <= 0 || remaining >= REFRESH_THRESHOLD_MS) return
    if (refreshing) return
    refreshing = true
    try {
      const baseUrl = getConfig().apiBaseUrl
      const response = await axios.post(baseUrl + '/auth/refresh', null, {
        withCredentials: true,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      const expiresHeader = response.headers['x-session-expires']
      if (expiresHeader) {
        sessionExpiresAt.value = Number(expiresHeader)
      }
    } catch {
      // refresh failed silently
    } finally {
      refreshing = false
    }
  }

  function onActivity() {
    tryRefresh()
  }

  function startListening() {
    for (const event of events) {
      window.addEventListener(event, onActivity, { passive: true })
    }
  }

  function stopListening() {
    for (const event of events) {
      window.removeEventListener(event, onActivity)
    }
  }

  onUnmounted(() => {
    stopListening()
  })
  return { startListening, stopListening }
}
